/**
 * SubscriptionPaywall — the single purchase surface for Per Diem.
 *
 * Owns the full RevenueCat purchase lifecycle: offering load, package
 * selection, renewal acknowledgment, checkout, entitlement activation, and
 * conversion analytics. Used in two shells:
 *  - Settings: modal presentation ('default' offering — no free trial, so
 *    lapsed users can't claim a second one)
 *  - Onboarding gate: inline presentation ('free_trial' offering — the
 *    card-upfront trial for new households)
 */

import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SUBSCRIPTION_ACKNOWLEDGMENT_TEXT } from '@perdiem/trpc/legal';
import { supabase } from '../lib/supabase';
import { trpc } from '../lib/trpc';
import {
  addEntitlementStatusListener,
  configureRevenueCat,
  getPackages,
  isUserCancelledError,
  purchase,
  waitForActiveEntitlement,
} from '../lib/purchases';
import type { RCPackage } from '../lib/purchases';
import {
  getDefaultSubscriptionPackageId,
  getFreeTrialPaywallCopy,
  getSubscriptionPackagePresentation,
  orderSubscriptionPackagesForPaywall,
} from '../lib/subscription-presentation';
import { trackSubscriptionConversionEvent } from '../lib/product-events';
import { getLegalClientContext } from '../lib/legal-client';
import { getLegalUrl, openExternalUrl } from '../lib/utils';
import { useColors } from '../lib/colors';
import { Button } from './Button';
import { ResponsiveModal } from './ResponsiveModal';

const PRIVACY_POLICY_URL = getLegalUrl('privacy');
const TERMS_OF_SERVICE_URL = getLegalUrl('terms');

const PURCHASE_RECOVERY_WAIT = {
  attempts: 15,
  delayMs: 1000,
  forceRefresh: true,
} as const;

export type SubscriptionPaywallTrigger =
  | 'onboarding_trial_gate'
  | 'restricted_access'
  | 'subscribe_deep_link'
  | 'settings';

interface SubscriptionPaywallProps {
  visible: boolean;
  trigger: SubscriptionPaywallTrigger;
  offeringIdentifier: string;
  onClose: () => void;
  /** Called after the entitlement is active and synced to the API. */
  onActivated: () => void;
  /** 'modal' wraps content in ResponsiveModal; 'inline' renders flat for full-screen shells. */
  presentation?: 'modal' | 'inline';
  opaqueBackdrop?: boolean;
  /** Shown when no trial-specific heading applies (settings passes its state-aware copy). */
  fallbackHeading?: string;
  fallbackDescription?: string;
}

export function SubscriptionPaywall({
  visible,
  trigger,
  offeringIdentifier,
  onClose,
  onActivated,
  presentation = 'modal',
  opaqueBackdrop = false,
  fallbackHeading = 'Subscribe to Per Diem',
  fallbackDescription = 'Choose a subscription to keep using Per Diem.',
}: SubscriptionPaywallProps) {
  const c = useColors();
  const utils = trpc.useUtils();
  const subscriptionQ = trpc.subscription.getStatus.useQuery(undefined, { enabled: visible });
  const accountsQ = trpc.accounts.list.useQuery(undefined, { enabled: visible });
  const accountCount = accountsQ.data?.length ?? 0;
  const subscription = subscriptionQ.data;
  const isPromoTrial =
    subscription?.accessKind === 'trial_promo'
    || subscription?.accessKind === 'trial_grace'
    || subscription?.accessKind === 'expired_trial_promo';
  const isAccessRestricted = Boolean(subscription && !subscription.isActive);

  const syncEntitlementMutation = trpc.subscription.syncEntitlement.useMutation({
    onSuccess: () => {
      void Promise.all([
        utils.bootstrap.get.invalidate(),
        utils.subscription.getStatus.invalidate(),
      ]);
    },
  });
  const acceptSubscriptionTermsMutation = trpc.legal.acceptSubscriptionTerms.useMutation();

  const [packages, setPackages] = useState<RCPackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [acceptedSubscriptionRenewal, setAcceptedSubscriptionRenewal] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const purchaseCompletionInFlightRef = useRef(false);
  const purchaseCompletedTrackedRef = useRef(false);
  const openedRef = useRef(false);

  const selectedPackage = packages.find((pkg) => pkg.id === selectedPackageId) ?? null;
  const trialCopy = selectedPackage
    ? getFreeTrialPaywallCopy(selectedPackage)
    : null;

  const trackPaywallEvent = (
    eventName: Parameters<typeof trackSubscriptionConversionEvent>[0]['eventName'],
    details: Partial<Omit<
      Parameters<typeof trackSubscriptionConversionEvent>[0],
      'eventName' | 'trigger' | 'accessKind' | 'isAccessRestricted' | 'isPromoTrial'
    >> = {},
  ) => {
    trackSubscriptionConversionEvent({
      eventName,
      trigger,
      accessKind: subscription?.accessKind,
      isAccessRestricted,
      isPromoTrial,
      accountCount: accountsQ.data?.length ?? null,
      ...details,
    });
  };

  const trackPurchaseCompletedOnce = (pkg?: RCPackage | null) => {
    if (purchaseCompletedTrackedRef.current) return;
    purchaseCompletedTrackedRef.current = true;
    trackPaywallEvent('subscription.purchase_completed', {
      packageId: pkg?.id ?? selectedPackageId,
      packageType: pkg?.packageType ?? null,
    });
  };

  const loadPackages = async () => {
    setPurchaseError(null);
    setAcceptedSubscriptionRenewal(false);
    setSelectedPackageId(null);
    // Clear any purchase attempt that never settled (e.g. an abandoned
    // checkout) so the subscribe button can't stay stuck in a loading state.
    setPurchasing(false);
    purchaseCompletedTrackedRef.current = false;
    setLoadingPackages(true);
    trackPaywallEvent('subscription.paywall_opened');
    try {
      const { data } = await supabase.auth.getSession();
      const revenueCatCustomerId = data.session?.user.id;
      if (revenueCatCustomerId) {
        configureRevenueCat(revenueCatCustomerId);
      }

      const pkgs = await getPackages({ offeringIdentifier });
      if (pkgs.length === 0) {
        setPackages([]);
        setSelectedPackageId(null);
        setPurchaseError(`Could not load RevenueCat subscription options. Check the ${offeringIdentifier} offering and refresh.`);
        trackPaywallEvent('subscription.paywall_packages_failed', {
          packageCount: 0,
          failureStage: 'offering_load',
          errorBucket: 'empty_offering',
        });
        return;
      }
      const orderedPackages = orderSubscriptionPackagesForPaywall(pkgs);
      setPackages(orderedPackages);
      const defaultPackageId = getDefaultSubscriptionPackageId(orderedPackages);
      const defaultPackage = orderedPackages.find((pkg) => pkg.id === defaultPackageId) ?? null;
      setSelectedPackageId(defaultPackageId);
      trackPaywallEvent('subscription.paywall_packages_loaded', {
        packageCount: pkgs.length,
      });
      if (defaultPackage) {
        trackPaywallEvent('subscription.paywall_plan_selected', {
          packageId: defaultPackage.id,
          packageType: defaultPackage.packageType,
          selectionSource: 'default',
        });
      }
    } catch (e) {
      if (__DEV__) {
        console.warn('[RevenueCat] failed to load offering', offeringIdentifier, e);
      }
      setPackages([]);
      setSelectedPackageId(null);
      const devDetail = __DEV__ && e instanceof Error ? ` (${e.message})` : '';
      setPurchaseError(`Could not load RevenueCat subscription options${devDetail}. Check the local web key and refresh.`);
      trackPaywallEvent('subscription.paywall_packages_failed', {
        failureStage: 'offering_load',
        errorBucket: 'package_load_error',
      });
    } finally {
      setLoadingPackages(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      openedRef.current = false;
      purchaseCompletionInFlightRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;
    void loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Native purchases can activate via the RevenueCat listener before our
  // purchase promise resolves — treat listener activation as completion.
  useEffect(() => {
    if (!visible) return;
    if (isPromoTrial) return;

    return addEntitlementStatusListener((status) => {
      if (!status.isActive || purchaseCompletionInFlightRef.current) return;

      purchaseCompletionInFlightRef.current = true;
      setPurchaseError(null);

      void (async () => {
        try {
          const { data } = await supabase.auth.getSession();
          const revenueCatCustomerId = data.session?.user.id;
          if (revenueCatCustomerId) {
            await syncEntitlementMutation.mutateAsync({ revenueCatCustomerId });
          }
          trackPurchaseCompletedOnce(selectedPackage);
          void utils.subscription.getStatus.invalidate();
          onActivated();
        } catch {
          purchaseCompletionInFlightRef.current = false;
        }
      })();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPromoTrial, visible, syncEntitlementMutation, utils.subscription.getStatus]);

  const handlePurchase = async (pkg: RCPackage) => {
    setPurchasing(true);
    setPurchaseError(null);
    purchaseCompletionInFlightRef.current = false;
    try {
      if (!acceptedSubscriptionRenewal) {
        setPurchaseError('Please acknowledge the auto-renewing subscription terms to continue.');
        trackPaywallEvent('subscription.checkout_failed', {
          packageId: pkg.id,
          packageType: pkg.packageType,
          failureStage: 'validation',
          errorBucket: 'terms_unaccepted',
        });
        return;
      }

      trackPaywallEvent('subscription.checkout_started', {
        packageId: pkg.id,
        packageType: pkg.packageType,
      });

      await acceptSubscriptionTermsMutation.mutateAsync(getLegalClientContext('subscription'));

      if (__DEV__ && pkg.id.startsWith('dev-')) {
        setPurchaseError('RevenueCat packages are not loaded in this local session. Refresh the app and try again.');
        trackPaywallEvent('subscription.checkout_failed', {
          packageId: pkg.id,
          packageType: pkg.packageType,
          failureStage: 'configuration',
          errorBucket: 'development_package',
        });
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const userEmail = sessionData.session?.user.email ?? undefined;

      if (!isPromoTrial) {
        const existingStatus = await waitForActiveEntitlement({ attempts: 1, delayMs: 0 });
        if (existingStatus.isActive) {
          purchaseCompletionInFlightRef.current = true;
          const revenueCatCustomerId = sessionData.session?.user.id;
          if (revenueCatCustomerId) {
            await syncEntitlementMutation.mutateAsync({ revenueCatCustomerId });
          }
          onActivated();
          return;
        }
      }

      const purchaseResult = await purchase(pkg.id, userEmail);
      if (!purchaseResult.isActive) {
        const activationStatus = await waitForActiveEntitlement(PURCHASE_RECOVERY_WAIT);
        if (!activationStatus.isActive) {
          if (purchaseCompletionInFlightRef.current) return;
          setPurchaseError('Purchase did not activate. Please try again.');
          trackPaywallEvent('subscription.checkout_failed', {
            packageId: pkg.id,
            packageType: pkg.packageType,
            failureStage: 'entitlement_activation',
            errorBucket: 'activation_timeout',
          });
          return;
        }
      }
      purchaseCompletionInFlightRef.current = true;
      await syncEntitlementMutation.mutateAsync({
        revenueCatCustomerId: purchaseResult.revenueCatCustomerId,
      });
      trackPurchaseCompletedOnce(pkg);
      void utils.subscription.getStatus.invalidate();
      onActivated();
    } catch (e) {
      if (isUserCancelledError(e)) {
        trackPaywallEvent('subscription.checkout_canceled', {
          packageId: pkg.id,
          packageType: pkg.packageType,
          failureStage: 'checkout',
          errorBucket: 'user_canceled',
        });
      } else {
        if (!isPromoTrial) {
          try {
            const { isActive } = await waitForActiveEntitlement(PURCHASE_RECOVERY_WAIT);
            if (isActive) {
              purchaseCompletionInFlightRef.current = true;
              const { data } = await supabase.auth.getSession();
              const revenueCatCustomerId = data.session?.user.id;
              if (revenueCatCustomerId) {
                await syncEntitlementMutation.mutateAsync({ revenueCatCustomerId });
              }
              trackPurchaseCompletedOnce(pkg);
              onActivated();
              return;
            }
          } catch {
            // Fall through to the original purchase error.
          }
        }

        if (purchaseCompletionInFlightRef.current) return;
        setPurchaseError(e instanceof Error ? e.message : 'Purchase failed');
        trackPaywallEvent('subscription.checkout_failed', {
          packageId: pkg.id,
          packageType: pkg.packageType,
          failureStage: 'checkout',
          errorBucket: 'purchase_error',
        });
      }
    } finally {
      setPurchasing(false);
    }
  };

  const handleConfirmSubscription = () => {
    if (!selectedPackage) {
      setPurchaseError('Choose a subscription plan to continue.');
      return;
    }

    void handlePurchase(selectedPackage);
  };

  const heading = isAccessRestricted && trigger !== 'onboarding_trial_gate'
    ? 'Pick up right where you left off'
    : isPromoTrial && trigger !== 'onboarding_trial_gate'
      ? 'Keep everything running'
      : trialCopy
        ? trialCopy.heading
        : fallbackHeading;
  const description = trialCopy
    ? trialCopy.description
    : fallbackDescription;

  const content = (
    <View className="gap-3 px-5 pb-5 pt-2">
      {trialCopy ? (
        <View className="items-center gap-1 py-1">
          <View className="rounded-full bg-green-100 px-3 py-1.5 dark:bg-green-900/40">
            <Text className="text-xs font-semibold text-forest-700 dark:text-forest-500">
              {trialCopy.badgeLabel}
            </Text>
          </View>
          <Text className="text-center text-[11px] text-gray-500 dark:text-gray-400">
            {trialCopy.paymentMethodNote}
          </Text>
        </View>
      ) : null}
      <View className="gap-2 rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800">
        {[
          accountCount > 0
            ? `Your ${accountCount} connected ${accountCount === 1 ? 'account' : 'accounts'}, syncing automatically`
            : 'Accounts sync automatically — balances, transactions, holdings',
          'A daily spending number that keeps you on pace',
          'Your FIRE plan, recalculated as life changes',
          'An AI advisor that knows your full financial picture',
        ].map((valueProp) => (
          <View key={valueProp} className="flex-row items-start gap-2">
            <Ionicons name="checkmark-circle" size={16} color={c.primary} style={{ marginTop: 1 }} />
            <Text className="flex-1 text-xs leading-5 text-gray-700 dark:text-gray-200">
              {valueProp}
            </Text>
          </View>
        ))}
      </View>

      {loadingPackages && <ActivityIndicator size="small" color={c.primary} className="my-4" />}

      {!loadingPackages && packages.length === 0 && !purchaseError && (
        <Text className="text-sm text-gray-400 text-center py-4">No packages available</Text>
      )}

      {packages.map((pkg) => {
        const isSelected = pkg.id === selectedPackageId;
        const pkgPresentation = getSubscriptionPackagePresentation(pkg, packages);

        return (
          <Pressable
            key={pkg.id}
            onPress={() => {
              setSelectedPackageId(pkg.id);
              setPurchaseError(null);
              trackPaywallEvent('subscription.paywall_plan_selected', {
                packageId: pkg.id,
                packageType: pkg.packageType,
                selectionSource: 'user',
              });
            }}
            disabled={purchasing}
            className={`rounded-xl border p-4 ${
              isSelected
                ? 'border-forest-500 bg-green-50 dark:bg-green-950/50'
                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
            }`}
          >
            <View className="flex-row items-start justify-between gap-3">
              <View className="flex-1 flex-row items-start gap-3">
                <View
                  className={`mt-0.5 h-5 w-5 items-center justify-center rounded-full border ${
                    isSelected ? 'border-forest-500 bg-forest-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {isSelected ? <Ionicons name="checkmark" size={13} color="#ffffff" /> : null}
                </View>
                <View className="flex-1">
                  <View className="flex-row flex-wrap items-center gap-2">
                    <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">{pkgPresentation.label}</Text>
                    {pkgPresentation.badge ? (
                      <View className="rounded-full bg-green-100 px-2 py-0.5 dark:bg-green-900/40">
                        <Text className="text-[11px] font-semibold text-forest-700 dark:text-forest-500">
                          {pkgPresentation.badge}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                  {pkgPresentation.comparisonLabel ? (
                    <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {pkgPresentation.comparisonLabel}
                    </Text>
                  ) : null}
                  {pkgPresentation.description ? (
                    <Text className="mt-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {pkgPresentation.description}
                    </Text>
                  ) : null}
                </View>
              </View>
              <Text className="text-lg font-bold text-green-700 dark:text-green-500">
                {pkgPresentation.priceLabel}
              </Text>
            </View>
          </Pressable>
        );
      })}

      {purchaseError && (
        <Text className="text-xs text-red-600">{purchaseError}</Text>
      )}

      <Pressable
        onPress={() => {
          setAcceptedSubscriptionRenewal((value) => !value);
          setPurchaseError(null);
        }}
        className="flex-row gap-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800"
      >
        <View
          className={`mt-0.5 h-5 w-5 items-center justify-center rounded border ${
            acceptedSubscriptionRenewal ? 'border-forest-500 bg-forest-600' : 'border-gray-400'
          }`}
        >
          {acceptedSubscriptionRenewal ? <Ionicons name="checkmark" size={14} color="#ffffff" /> : null}
        </View>
        <Text className="flex-1 text-xs leading-5 text-gray-600 dark:text-gray-300">
          {trialCopy
            ? trialCopy.acknowledgment
            : SUBSCRIPTION_ACKNOWLEDGMENT_TEXT}
        </Text>
      </Pressable>

      <Button
        size="sm"
        className="mt-1"
        onPress={handleConfirmSubscription}
        loading={purchasing}
        disabled={!selectedPackage || !acceptedSubscriptionRenewal || loadingPackages}
      >
        {trialCopy
          ? trialCopy.ctaLabel
          : isPromoTrial
            ? 'Keep My Plan Active'
            : 'Subscribe'}
      </Button>
      <Text className="text-center text-[11px] text-gray-400 dark:text-gray-500">
        {trialCopy
          ? trialCopy.reassurance
          : 'Cancel anytime from Settings.'}
      </Text>

      <View className="flex-row flex-wrap items-center justify-center gap-4">
        <Pressable onPress={() => openExternalUrl(TERMS_OF_SERVICE_URL)}>
          <Text className="text-xs font-medium text-green-700">Terms of Service</Text>
        </Pressable>
        <Pressable onPress={() => openExternalUrl(PRIVACY_POLICY_URL)}>
          <Text className="text-xs font-medium text-green-700">Privacy Policy</Text>
        </Pressable>
      </View>
    </View>
  );

  if (presentation === 'inline') {
    if (!visible) return null;
    return (
      <View className="w-full max-w-[420px] self-center">
        <View className="px-5 pt-2 pb-1">
          <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100">{heading}</Text>
          <Text className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</Text>
        </View>
        {content}
      </View>
    );
  }

  return (
    <ResponsiveModal
      visible={visible}
      onClose={onClose}
      maxWidth={420}
      maxHeight={680}
      opaqueBackdrop={opaqueBackdrop}
      header={
        <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {heading}
        </Text>
      }
      subtitle={
        <Text className="text-sm text-gray-500 dark:text-gray-400">
          {description}
        </Text>
      }
    >
      <ScrollView className="flex-shrink">
        {content}
      </ScrollView>
    </ResponsiveModal>
  );
}
